import { useEffect, useState } from "react";
import { Button, Checkbox, Empty, Modal, Spin, Tag, message } from "antd";
import {
  getCustomerCountryEmployeesForOwnerChange,
  reassignCustomerCountryAccountOwner,
} from "../../services/ApiService";

// Country-scoped version of AccountOwnerChangeModal.js -- confirmed
// with the user, 2026-10-16: a customer with employees across more than
// one country can have a different account owner per country. Opens
// from CustomerCountryDetailModal's per-country "Reassign" action.
// Reassigning here never touches the customer's base account_owner
// (the fallback for any country with no override) -- only this one
// country's own override row.
const CountryAccountOwnerChangeModal = ({
  open,
  customerId,
  customerName,
  countryId,
  countryName,
  newOwnerId,
  newOwnerLabel,
  onClose,
  onSaved,
}) => {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !customerId || !countryId) return;

    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const res = await getCustomerCountryEmployeesForOwnerChange(customerId, countryId);
        if (res?.responseCode === 200) {
          const list = res.employees || [];
          setEmployees(list);
          setSelectedIds(
            new Set(
              list
                .filter((e) => !e.is_exited && e.current_owner_id && e.current_owner_id === res.current_account_owner)
                .map((e) => e.onboard_form_id)
            )
          );
        } else {
          message.error(res?.responseMessage || "Could not load this country's employees.");
        }
      } catch (error) {
        console.error("Error fetching country employees:", error);
        message.error("Could not load this country's employees.");
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, [open, customerId, countryId]);

  const toggleEmployee = (onboardFormId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(onboardFormId);
      else next.delete(onboardFormId);
      return next;
    });
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const res = await reassignCustomerCountryAccountOwner(customerId, countryId, {
        new_owner_id: newOwnerId,
        employee_onboard_form_ids: Array.from(selectedIds),
      });
      if (res?.responseCode === 200) {
        message.success(
          selectedIds.size
            ? `${countryName} account owner updated — ${selectedIds.size} employee(s) moved to ${newOwnerLabel}.`
            : `${countryName} account owner updated.`
        );
        onSaved(newOwnerId);
        onClose();
      } else {
        message.error(res?.responseMessage || "Failed to reassign account owner.");
      }
    } catch (error) {
      console.error("Error reassigning country account owner:", error);
      message.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Reassign Account Owner — ${customerName || ""} (${countryName || ""})`}
      open={open}
      onCancel={onClose}
      width={620}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="confirm" type="primary" loading={saving} onClick={handleConfirm}>
          Confirm — set to {newOwnerLabel}
        </Button>,
      ]}
    >
      <p className="owner-change-hint">
        New owner for <strong>{countryName}</strong>: <strong>{newOwnerLabel}</strong>. Pick which
        of this country's employees under this customer should also move — anyone left unchecked
        keeps their current owner. Exited employees can't be moved.
      </p>

      {loading ? (
        <div style={{ textAlign: "center", padding: "1.5rem" }}>
          <Spin />
        </div>
      ) : employees.length === 0 ? (
        <Empty description="No employees found for this country under this customer." />
      ) : (
        <div className="owner-change-employee-list">
          {employees.map((emp) => (
            <div key={emp.onboard_form_id} className="owner-change-employee-row">
              <Checkbox
                checked={selectedIds.has(emp.onboard_form_id)}
                disabled={emp.is_exited}
                onChange={(e) => toggleEmployee(emp.onboard_form_id, e.target.checked)}
              >
                {emp.employee_name}
              </Checkbox>
              <Tag color={emp.is_exited ? "default" : "blue"}>{emp.status || "—"}</Tag>
              <span className="owner-change-current-owner">
                {emp.current_owner_name || "Unassigned"}
              </span>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default CountryAccountOwnerChangeModal;
